import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Clock, X } from 'lucide-react';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/inventario`;

/**
 * Aviso del estado del timbrado, visible desde cualquier pantalla.
 *
 * El aviso ya existía, pero dentro de Configuración → Facturación: había que
 * ir a buscarlo. Eso no sirve para lo que tiene que servir, porque nadie entra
 * a esa pantalla salvo cuando ya hay un problema.
 *
 * Importa porque un timbrado vencido no se resuelve rápido: conseguir uno
 * nuevo ante la administración tributaria lleva días. Avisar el mismo día en
 * que vence es avisar tarde. Por eso aparece **un mes antes**, que es el plazo
 * con el que se puede gestionar sin apuro.
 *
 * También avisa cuando se está por agotar el rango de numeración, que es el
 * mismo problema por otro camino: sin números disponibles no se puede emitir,
 * aunque el timbrado siga vigente.
 */

// Un mes de anticipación para el vencimiento, y los últimos cincuenta números
// para el rango. Los dos son plazos para gestionar, no para enterarse.
const DIAS_DE_AVISO = 30;
const NUMEROS_DE_AVISO = 50;

const DIA = 24 * 60 * 60 * 1000;

/**
 * Qué decir sobre un timbrado, o null si no hay nada que decir.
 *
 * Separado del componente para poder probarlo sin dibujar nada: lo que importa
 * verificar son los umbrales y los mensajes, no el HTML.
 */
export function estadoTimbrado(config, hoy = new Date()) {
    if (!config || !config.fecha_vencimiento) {
        return {
            nivel: 'error',
            texto: 'No hay timbrado cargado: las ventas se registran pero no se facturan. '
                 + 'Cargalo en Configuración → Facturación.',
        };
    }

    // Se cuentan días de calendario, no horas. Un timbrado vence al terminar
    // su último día, así que comparar instantes hace que a las 10 de la mañana
    // del día del vencimiento «falte medio día» —y, peor, que uno vencido ayer
    // dé una diferencia negativa pequeña que se redondea a cero—.
    const dias = Math.round((soloFecha(config.fecha_vencimiento) - aMedianoche(hoy)) / DIA);

    if (dias < 0) {
        return {
            nivel: 'error',
            texto: `El timbrado ${config.timbrado} venció el ${formatear(config.fecha_vencimiento)}. `
                 + 'Hasta que cargues el nuevo, las ventas quedan sin factura.',
        };
    }

    const restantes = (config.numero_hasta ?? 0) - (config.numero_actual ?? 0) + 1;
    if (restantes <= 0) {
        return {
            nivel: 'error',
            texto: `Se agotó el rango de numeración del timbrado ${config.timbrado}. `
                 + 'Hasta que cargues el nuevo, las ventas quedan sin factura.',
        };
    }

    if (dias <= DIAS_DE_AVISO) {
        const cuando = dias === 0
            ? 'vence hoy'
            : `vence el ${formatear(config.fecha_vencimiento)}, en ${dias} día${dias === 1 ? '' : 's'}`;
        return {
            nivel: 'alerta',
            texto: `El timbrado ${config.timbrado} ${cuando}. Gestioná el nuevo con tiempo: `
                 + 'conseguirlo lleva varios días.',
        };
    }

    if (restantes <= NUMEROS_DE_AVISO) {
        return {
            nivel: 'alerta',
            texto: `Quedan ${restantes} número${restantes === 1 ? '' : 's'} disponibles en el `
                 + `timbrado ${config.timbrado}. Cuando se agoten, las ventas quedan sin factura.`,
        };
    }

    return null;
}

/** La fecha del timbrado (YYYY-MM-DD) como medianoche local. */
function soloFecha(texto) {
    const [anio, mes, dia] = String(texto).split('-').map(Number);
    return new Date(anio, mes - 1, dia);
}

/** El momento dado, reducido al día que es. */
function aMedianoche(momento) {
    return new Date(momento.getFullYear(), momento.getMonth(), momento.getDate());
}

function formatear(fecha) {
    return new Date(`${fecha}T12:00:00`).toLocaleDateString('es-PY');
}

export default function AvisoTimbrado() {
    const [config, setConfig] = useState(undefined);  // undefined = cargando
    const [oculto, setOculto] = useState(false);

    useEffect(() => {
        axios.get(`${API}/facturacion-config/`)
            .then(r => setConfig(r.data))
            // Si la consulta falla no se inventa un estado: callar es mejor que
            // avisar de un problema que no se sabe si existe.
            .catch(() => setConfig(undefined));
    }, []);

    if (config === undefined || oculto) return null;

    const estado = estadoTimbrado(config);
    if (!estado) return null;

    const esError = estado.nivel === 'error';
    const estilo = esError
        ? 'bg-error-50 border-error-200 dark:bg-error-950/40 dark:border-error-800 text-error-800 dark:text-error-300'
        : 'bg-alerta-50 border-alerta-200 dark:bg-alerta-950/40 dark:border-alerta-800 text-alerta-800 dark:text-alerta-300';

    return (
        <div className={`mx-8 mt-4 flex items-start gap-3 px-4 py-3 rounded-xl border text-sm ${estilo}`}>
            {esError
                ? <AlertTriangle size={16} className="text-error-500 shrink-0 mt-0.5" />
                : <Clock size={16} className="text-alerta-500 shrink-0 mt-0.5" />}
            <p className="flex-1 font-medium">{estado.texto}</p>
            <button
                type="button"
                onClick={() => setOculto(true)}
                title="Ocultar hasta la próxima vez que entres"
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 shrink-0"
            >
                <X size={14} />
            </button>
        </div>
    );
}
